import * as React from 'react';
import classnames from 'classnames';

import { Link } from 'libs/router';

import Tooltip from 'components/Tooltip';

import { __t, applyTranslations } from 'utils/language';
import { replaceString } from 'utils/helper/common';
import { getCloudinaryImageUrl } from 'utils/cloudinary';

import type { CampaignAchievementMetric } from 'types';

applyTranslations(require('./campaign-achievements-card.i18n.json'));

type Props = {
  type: string,
  entityId?: number,
  imageUrl?: string,
  thumbnailUrl?: string,
  title?: string,
  subtitle?: string,
  metrics?: Array<CampaignAchievementMetric>,
  showDefault?: boolean
};

class CampaignAchievementsCard extends React.PureComponent<Props> {
  formatValue(id: string, value: number): number | string {
    if (id === 'joined_campaigns') {
      return value;
    }

    return `${value}%`;
  }

  getTooltipMessage(id: string): string {
    return __t(`campaign_achievements_card.${id}_tooltip`);
  }

  getLink(type: string, entityId?: number): string | undefined | null {
    if (!entityId) {
      return null;
    }
    if (type === 'campaign') {
      return `/campaigns/${entityId}/participants`;
    }
    if (type === 'content_creator') {
      return `/content-creator/profiles/${entityId}`;
    }
    return `/campaigns/${entityId}/report/v2`;
  }

  renderCard(): React.ReactNode {
    const { type: id, imageUrl, thumbnailUrl, title, subtitle, metrics, showDefault } = this.props;
    const type = replaceString(id, '_', '-');
    const cloudinaryUrl = getCloudinaryImageUrl({ url: thumbnailUrl || imageUrl || '' }, { width: 260, height: 104 });
    const cardClasses = classnames({
      'campaign-achievements-card': true,
      'campaign-achievements-card--default': showDefault
    });
    const headerIconClasses = classnames({
      'campaign-achievements-card__header-icon': true,
      [`campaign-achievements-card__header-icon--type-${type}`]: type
    });
    const headerContentClasses = classnames({
      'campaign-achievements-card__header-content': true,
      'campaign-achievements-card__header-content--default': showDefault,
      'campaign-achievements-card__header-content--empty-image': !showDefault && !cloudinaryUrl
    });

    return (
      <div className={cardClasses}>
        <div className="campaign-achievements-card__header">
          <div className={headerIconClasses} />
          <div
            className={headerContentClasses}
            style={cloudinaryUrl ? { backgroundImage: `url(${cloudinaryUrl})` } : undefined}
            data-hj-suppress
          >
            {
              showDefault &&
                <span>{__t('campaign_achievements_card.empty_card_message')}</span>
            }
            {
              !showDefault && !cloudinaryUrl &&
                <span>{__t('campaign_achievements_card.empty_image_message')}</span>
            }
          </div>
        </div>
        {
          !showDefault &&
            <div className="campaign-achievements-card__content">
              <div
                className="campaign-achievements-card__content-header"
                data-hj-suppress
              >
                <h3 className="campaign-achievements-card__title">{title ?? ''}</h3>
                {
                  subtitle &&
                    <h3 className="campaign-achievements-card__sub-title">{subtitle}</h3>
                }
              </div>
              <div className="campaign-achievements-card__metrics">
                {
                  metrics?.map((metric) => (
                    <div
                      key={`type-${metric.label}`}
                      className="campaign-achievements-card__metric"
                    >
                      <div className="campaign-achievements-card__metric-label">
                        {metric.label}
                      </div>
                      <div className="campaign-achievements-card__metric-value">
                        {this.formatValue(metric.id, metric.value)}
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const { showDefault, type, entityId } = this.props;
    const card = this.renderCard();

    if (showDefault) {
      return card;
    }

    return (
      <Tooltip
        message={this.getTooltipMessage(type)}
        display="block"
      >
        <Link
          to={this.getLink(type, entityId)}
          target="_blank"
          className="campaign-achievements-card__link"
        >
          {card}
        </Link>
      </Tooltip>
    );
  }
}

export default CampaignAchievementsCard;
